import store from '@/base/store'
import { PAYWAY_GROUPS_TYPES } from '@/base/const/payway.const'

export class PaymentFee {
  /**
   * Funkcja zwracajaca dane transakcyjne dla widoku paywalla
   * @returns {*}
   */
  static get paywallPaymentData () {
    return store.state.payment.paymentData
  }

  /**
   * Funkcja zwracajaca dane transakcji dla confirmation page
   * @returns {*}
   */
  static get confirmationTransactionData () {
    return store.state.confirmation.data
  }

  /**
   * Funkcja sprawdzająca czy mamy dlane dla paywalla
   * @returns {boolean}
   */
  static isPaywallView () {
    return PaymentFee.paywallPaymentData !== null
  }

  /**
   * Funkcja sprawdzająca czy mamy dane dla confirmation page
   * @returns {boolean}
   */
  static isConfirmationView () {
    return PaymentFee.confirmationTransactionData !== null
  }

  /**
   * Funkcja zwracajaca informacje czy wyświetlanie prowizji jest aktywne (na podstawie flagi)
   * @returns {*|boolean}
   */
  static isPaymentFeeActive () {
    const data = PaymentFee.isPaywallView() ? PaymentFee.paywallPaymentData : PaymentFee.confirmationTransactionData
    // sprawdzamy store i flagę - czy wyświetlać prowizję - bez tego mamy model merchanta a nie płatnika
    if (!data || !data?.visibilityOptions) return false
    return data.visibilityOptions.showFrontFees
  }

  /**
   * Funkcja sprawdzająca czy dane startowe mają prowizję w formacie legacy
   * (customerFee na głównym poziomie) — w tym formacie payAmount zawiera już prowizję
   * @returns {boolean}
   */
  static isPaymentFeeLegacyMode () {
    const data = PaymentFee.paywallPaymentData
    return data != null && data.customerFee !== undefined
  }

  /**
   * Funkcja sprawdzająca czy prowizje z różnych kanałów płatności mają inne stawki - tylko dla widoku paywalla
   * @returns {boolean}
   */
  static isPaymentFeeDifferent () {
    if (PaymentFee.isConfirmationView()) {
      return false
    }

    const feeList = PaymentFee._flatFeeList(PaymentFee.paywallPaymentData)
    return new Set(feeList).size > 1
  }

  /**
   * Funkcja zwracajaca pojedynczą kwotę prowizji - dla confirmation page i dla paywalla jeśli wszystkie prowizje są takie same
   * @returns {string|number|null}
   */
  static getPaymentFeeAsSingleValue () {
    if (PaymentFee.isConfirmationView()) {
      return Number(PaymentFee.confirmationTransactionData?.transaction.customerFee).toFixed(2).replace('.', ',')
    }

    if (PaymentFee.isPaymentFeeLegacyMode()) {
      return Number(PaymentFee.paywallPaymentData.customerFee).toFixed(2).replace('.', ',')
    }

    if (!PaymentFee.isPaymentFeeDifferent()) {
      const feeList = PaymentFee._flatFeeList(PaymentFee.paywallPaymentData)
      return Number(feeList[0]) || 0
    }
    return null
  }

  /**
   * Funkcja zwracająca prowizję dla wybranej grupy
   * @param group
   * @returns {string}
   */
  static getPaymentFeeForGroup (group) {
    const currency = PaymentFee.paywallPaymentData?.payAmountCurrency || 'PLN'
    const feeList = group.payways.map(payway => Number(payway.customerFee))
    const fmt = n => n.toFixed(2).replace('.', ',')
    if (feeList.length === 0) return fmt(0) + ' ' + currency
    const min = Math.min(...feeList)
    const max = Math.max(...feeList)
    return min === max ? fmt(min) + ' ' + currency : `${fmt(min)} - ${fmt(max)} ${currency}`
  }

  /**
   * Funkcja zwracająca prowizję dla wybranego kanału płatności - jako liczbę
   * @param payway
   * @returns {number}
   */
  static getPaymentFeeForPaywayAsNumber (payway) {
    return Number(payway.customerFee)
  }

  /**
   * Funkcja zwracająca prowizję dla aktualnie wybranego kanału płatności - jako liczbę
   * Używana przy inicjalizacji SDK (GooglePay, ApplePay, VisaCheckout, Click2Pay)
   * @returns {number}
   */
  static getPaymentFeeForActivePaywayAsNumber () {
    const paywayID = store.state.payment.form.paywayID
    if (!paywayID) return 0
    const payway = (PaymentFee.paywallPaymentData?.paywayGroups || [])
      .flatMap(group => group.payways || [])
      .find(p => p.id === paywayID)
    return payway ? Number(payway.customerFee) : 0
  }

  /**
   * Funkcja zwracająca prowizję dla wybranego kanału płatności - jako tekst
   * @param payway
   * @returns {string}
   */
  static getPaymentFeeForPayway (payway) {
    const currency = PaymentFee.paywallPaymentData?.payAmountCurrency || 'PLN'
    return Number(payway.customerFee).toFixed(2).replace('.', ',') + ' ' + currency
  }

  /**
   * Funkcja zwracająca posortowaną listę grup - zrzucamy BLIKa na koniec
   * @param groups
   * @returns {*[]}
   */
  static sortPaymentGroups (groups = []) {
    const idx = groups.findIndex(group => group.type === PAYWAY_GROUPS_TYPES.blik)
    if (idx === -1) return [...groups]
    return [...groups.slice(0, idx), ...groups.slice(idx + 1), groups[idx]]
  }

  static _flatFeeList (data) {
    return (data?.paywayGroups || [])
      .flatMap(group => group.payways || [])
      .map(payway => Number(payway.customerFee))
  }
}
